import type { DeploymentFingerprint } from "./types";

/** Extract version identity headers from a HEAD response. */
export function extractFingerprint(response: Response): DeploymentFingerprint {
  return {
    etag: normalizeHeaderValue(response.headers.get("etag")),
    lastModified: normalizeHeaderValue(response.headers.get("last-modified")),
  };
}

/** True when at least one usable version header is present. */
export function isFingerprintUsable(fingerprint: DeploymentFingerprint): boolean {
  return fingerprint.etag !== null || fingerprint.lastModified !== null;
}

/**
 * Compare two fingerprints for equality.
 * ETag is authoritative when both sides provide it; Last-Modified is the fallback.
 */
export function fingerprintsEqual(
  baseline: DeploymentFingerprint,
  current: DeploymentFingerprint,
): boolean {
  if (baseline.etag !== null && current.etag !== null) {
    return baseline.etag === current.etag;
  }

  if (baseline.lastModified !== null && current.lastModified !== null) {
    return baseline.lastModified === current.lastModified;
  }

  return (
    baseline.etag === current.etag &&
    baseline.lastModified === current.lastModified
  );
}

/** Returns true when the live fingerprint differs from the running baseline. */
export function hasDeploymentChanged(
  baseline: DeploymentFingerprint,
  current: DeploymentFingerprint,
): boolean {
  if (!isFingerprintUsable(current)) {
    return false;
  }

  return !fingerprintsEqual(baseline, current);
}

function normalizeHeaderValue(value: string | null): string | null {
  if (value === null) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
